import chroma from 'chroma-js'
import { EnumColorPreset } from '~/common/generated/apollo-operations'
import { gray, white } from '~/common/styles/colors'

/** A resolved color palette for one enum option. */
export interface ResolvedOptionColor {
  /** Multi-select chip background */
  fill: string
  /** Multi-select chip border */
  border: string
  /** Multi-select chip text + icon */
  content: string
  /** Dot indicator + single-select icon */
  vivid: string
}

type OklchChannels = [number, number, number]

/**
 * A preset by name, which is how it arrives from either of its two generated declarations: the GraphQL
 * enum when read from a column, or the Prisma string union when read from a form's stored options.
 */
export type EnumColorPresetName = `${EnumColorPreset}`

/**
 * Dot color per preset. Hues track ARP's palette, cited per row; lightness and chroma are set for a small
 * mark on a light surface. ARP's own values are authored as fills sitting behind `contrast-color()` ink, so
 * they are not reused directly. Source: `packages/arp/src/design-system/palette/color.css`
 */
const PRESET_ACCENT: Record<EnumColorPreset, OklchChannels> = {
  [EnumColorPreset.RED]: [0.623, 0.18, 20], // --ruby hue
  [EnumColorPreset.ORANGE]: [0.7, 0.17, 50], // --rust hue
  [EnumColorPreset.YELLOW]: [0.78, 0.15, 71.5], // --clementine hue, deepened for contrast
  [EnumColorPreset.GREEN]: [0.704, 0.13, 155], // --jade hue
  [EnumColorPreset.BLUE]: [0.65, 0.15, 268.4], // --blueberry hue
  [EnumColorPreset.PURPLE]: [0.595, 0.21, 290], // --amethyst hue
  [EnumColorPreset.GRAY]: [0.712, 0, 75], // ARP's neutral hue, inert at zero chroma
}

/**
 * The multi-select chip's three tones derive from the accent: each fixes a lightness and scales the
 * accent's chroma. Scaling rather than substituting keeps gray achromatic.
 */
const FILL_L = 0.925
const FILL_C_RATIO = 0.34
const BORDER_L = 0.83
const BORDER_C_RATIO = 0.55
const CONTENT_L = 0.41
const CONTENT_C_RATIO = 0.66

// A custom color is its own fill, so its border is derived by darkening that fill
const CUSTOM_BORDER_DL = 0.15

// Parses the backend "L C H" OKLCH channel string (no oklch() wrapper) into numeric channels
const parseOklchChannels = (channels: string): OklchChannels | null => {
  const parts = channels.trim().split(/\s+/).map(Number)
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
    return null
  }
  return [parts[0], parts[1], parts[2]]
}

const oklch = ([l, c, h]: OklchChannels): string =>
  `oklch(${l.toFixed(4)} ${c.toFixed(4)} ${h.toFixed(2)})`

// Darkens an OKLCH color by lowering lightness, floored at zero
const shade = ([l, c, h]: OklchChannels, dL: number): string => oklch([Math.max(l - dL, 0), c, h])

// Content tone for text and icons sitting on a custom color's own fill
const contrastOn = ([l, c, h]: OklchChannels): string =>
  chroma.oklch(l, c, h).luminance() > 0.5 ? gray[11] : white

/**
 * Lowers chroma until the color is one a screen can show, holding lightness and hue exact. A near-white
 * color has very little chroma available, so the chip's pale tones can ask for more than exists; left to the
 * browser it clamps each RGB channel separately, which lands on a different hue than the dot.
 */
const displayableChroma = (l: number, c: number, h: number): number => {
  if (!chroma.oklch(l, c, h).clipped()) {
    return c
  }
  let inside = 0
  let outside = c
  for (let i = 0; i < 20; i++) {
    const mid = (inside + outside) / 2
    if (chroma.oklch(l, mid, h).clipped()) {
      outside = mid
    } else {
      inside = mid
    }
  }
  return inside
}

// Re-tones an accent to a fixed lightness with scaled chroma, keeping the hue
const tone = ([, c, h]: OklchChannels, l: number, cRatio: number): string =>
  oklch([l, displayableChroma(l, c * cRatio, h), h])

const presetPalette = (accent: OklchChannels): ResolvedOptionColor => ({
  fill: tone(accent, FILL_L, FILL_C_RATIO),
  border: tone(accent, BORDER_L, BORDER_C_RATIO),
  content: tone(accent, CONTENT_L, CONTENT_C_RATIO),
  vivid: oklch(accent),
})

// Palette for an option with no color
export const NEUTRAL_ENUM_COLOR: ResolvedOptionColor = {
  fill: gray[2],
  border: gray[6],
  content: gray[10],
  vivid: gray[10],
}

const computeEnumOptionColor = (
  colorPreset?: EnumColorPresetName | null,
  colorCustom?: string | null,
): ResolvedOptionColor | null => {
  if (colorCustom) {
    const channels = parseOklchChannels(colorCustom)
    if (!channels) {
      return null
    }
    const fill = oklch(channels)
    return {
      fill,
      border: shade(channels, CUSTOM_BORDER_DL),
      content: contrastOn(channels),
      vivid: fill,
    }
  }
  if (colorPreset) {
    return presetPalette(PRESET_ACCENT[colorPreset])
  }
  return null
}

// Cached per color so the chroma work runs once
const paletteCache = new Map<string, ResolvedOptionColor | null>()

/**
 * Resolves an enum column option's color to a render-ready palette. A preset derives its tones from its
 * accent; a custom color is its own fill and dot, with contrast-derived chip content. Returns null when the
 * option has no color.
 */
export const resolveEnumOptionColor = (
  colorPreset?: EnumColorPresetName | null,
  colorCustom?: string | null,
): ResolvedOptionColor | null => {
  const key = colorCustom || colorPreset || ''
  const cached = paletteCache.get(key)
  if (cached !== undefined) {
    return cached
  }
  const palette = computeEnumOptionColor(colorPreset, colorCustom)
  paletteCache.set(key, palette)
  return palette
}

/** A preset's accent as hex, for seeding the custom color picker from the current selection */
export const presetAccentHex = (preset: EnumColorPreset): string =>
  chroma.oklch(...PRESET_ACCENT[preset]).hex()

/** The presets a user can pick from, each with the color name its translation key uses */
export const ENUM_COLOR_PRESETS = [
  { preset: EnumColorPreset.GRAY, name: 'gray' },
  { preset: EnumColorPreset.ORANGE, name: 'orange' },
  { preset: EnumColorPreset.YELLOW, name: 'yellow' },
  { preset: EnumColorPreset.GREEN, name: 'green' },
  { preset: EnumColorPreset.BLUE, name: 'blue' },
  { preset: EnumColorPreset.PURPLE, name: 'purple' },
  { preset: EnumColorPreset.RED, name: 'red' },
] as const

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

/**
 * Formats a color as the stored "L C H" channel string. The stored format admits only non-negative
 * fixed-point decimals, so channels are clamped and `toFixed` keeps exponent notation out of the output.
 * Lightness and chroma keep five decimals so that converting back to hex is exact.
 */
export const hexToOklchString = (color: string): string => {
  const [l, c, h] = chroma(color).oklch()
  const hue = Number.isNaN(h) ? 0 : ((h % 360) + 360) % 360
  return `${clamp(l, 0, 1).toFixed(5)} ${Math.max(c, 0).toFixed(5)} ${hue.toFixed(2)}`
}

/** Converts a stored "L C H" channel string back to hex for the color picker */
export const oklchStringToHex = (colorCustom: string): string | null => {
  const channels = parseOklchChannels(colorCustom)
  return channels ? chroma.oklch(...channels).hex() : null
}
